<template>
  <div v-if="shouldShowBanner" class="ld-relationships-banner">
    <v-layout
      justify-center
      align-center
      :style="{ 'background-color': color.background }"
    >
      <div class="banner-content">
        <v-icon small class="mr-1" :color="color.foreground">info</v-icon>
        <span :style="{ color: color.foreground }" v-html="bannerText"></span>
      </div>
      <v-btn small flat :color="color.foreground" @click="handleUpgradeNow">Update payment info to get started →</v-btn>
      <v-btn
        id="lw-checkout-banner-hide"
        icon
        small
        @click="handleHide"
      >
        <v-icon class="white-icon">close</v-icon>
      </v-btn>
    </v-layout>
  </div>
</template>

<script>
import { createNamespacedHelpers, mapGetters } from 'vuex';
import { HC_PROPS } from 'lion-vue-src/_globals';
import { startOfDay, addDays } from 'date-fns';

const { mapState: mapAppLayoutState, mapActions: mapAppLayoutActions } = createNamespacedHelpers(
  'ldAppLayout',
);

const sunsetMLS = ['HngTojeR1aC0f2IuR', 'AzyWC9RwRzp7C23sH', 'AzZcjYS2r29CE1ngw', 'LIONDESK1327605PLANABC123'];
const sunsetMLSSKUs = ['plan-liondesk-REColorado-monthly', 'liondesk-Alaska_MLS-monthly', 'plan-liondesk-real-comp-monthly', 'liondesk-realcomp-member-benefit', 'plan-v2-liondesk-entitlement-nwok-aor']

const nonSunsetMLS = ['16BcmCS3cUX8E1gG0','16BZALS3cTj2o1iHu','2smoc9EuRF9ud1K1Hxi','AzZMr8Syri06W110k', 'LIONDESK1327602PLANABC123', '1ps2gs9RV5mK7htPP'];
const nonSunsetMLSSKUs = ['plan-liondesk-CCAR-monthly', 'plan-liondesk-BayEast-monthly', 'plan-liondesk-CRMLS-monthly', 'plan-liondesk-v2-CAR-monthly', 'plan-v2-liondesk-entitlement-hawaii-aor', 'plan-liondesk-SDMLS-monthly'];


export default {
  props: {
    user: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      color: {
        foreground: '#1E3B8A',
        background: '#F0F6FF',
      },
      isHiddenCookie: null,
      clientsPoller: null,
      creatingRelationshipsLicense: false,
      // Map subscription IDs to coupon codes
      subscriptionCoupons: {
        'LIONDESK1327602PLANABC123': 'HAWAII',
        'HngTojeR1aC0f2IuR': 'REALCOMP',
        'AzZcjYS2r29CE1ngw': 'ALASKA',
        '1ps2gs9RV5mK7htPP': 'SDMLS',
        'AzyWC9RwRzp7C23sH': 'RECOLORADO'
      }
    };
  },
  computed: {
    ...mapAppLayoutState(['isMigrationComplete', 'isMigrationInProgress', 'relationshipsLicenseCreatedFound', 'ulToken', 'ulClientLicenses', 'hasRelationshipsOrder', 'createRelationshipsLicenseSuccess']),
    bannerText() {
      const subId = this.user && this.user.subscriptionOverview && this.user.subscriptionOverview.id_chargebee && this.user.subscriptionOverview.id_chargebee.String;
      const couponCode =
        this.user &&
        this.user.subscriptionOverview &&
        this.user.subscriptionOverview.id_chargebee &&
        this.subscriptionCoupons[this.user.subscriptionOverview.id_chargebee.String];

      console.log('[RELATIONSHIPS_BANNER] bannerText()', {
        subId,
        couponCode,
        creatingRelationshipsLicense: this.creatingRelationshipsLicense,
        sunsetDate: this.sunsetDate,
      });

      if (couponCode) {
        return this.generateBannerText(couponCode);
      }

      if (this.creatingRelationshipsLicense) {
        return `<b>Creating your Relationships license and beginning to migrate your LionDesk data...</b> 
          LionDesk will be available through <b>${this.sunsetDate}</b>, and your Relationships billing won’t begin until your current LionDesk subscription ends.`;
      }
      return `<b>Switch to Relationships—your new home for smarter client connections.</b> 
        LionDesk will be available through <b>${this.sunsetDate}</b>, and your Relationships billing won’t begin until your current LionDesk subscription ends.`;
    },
    nonSunsetMLSUser() {
      return this.user 
        && this.user.subscriptionOverview 
        && nonSunsetMLS.includes(this.user.subscriptionOverview.id_chargebee.String);
    },
    sunsetMLSUser() {
      return this.user 
        && this.user.subscriptionOverview 
        && sunsetMLS.includes(this.user.subscriptionOverview.id_chargebee.String)
    },
    isHawaiiMLS() {
      return this.user 
        && this.user.subscriptionOverview 
        && this.user.subscriptionOverview.id_chargebee.String === 'LIONDESK1327602PLANABC123';
    },
    sunsetDate() {
      if (this.sunsetMLSUser) {
        return 'September 30, 2025';
      }
      return 'October 31, 2025';
    },
    visibleBanner() {
      if (!this.user) {
        console.log('[RELATIONSHIPS_BANNER] visibleBanner(): no user');
        return false;
      }

      if (this.isHiddenCookie) {
        const displayDate = startOfDay(addDays(new Date(this.isHiddenCookie), 1));
        const beforeDisplay = new Date() < displayDate;
        console.log('[RELATIONSHIPS_BANNER] cookie gate', {
          isHiddenCookie: this.isHiddenCookie,
          displayDate,
          beforeDisplay,
        });
        if (beforeDisplay) {
          return false;
        }
        window.$cookies.remove('hideCheckoutLWBanner', null, HC_PROPS.MAIN_DOMAIN);
        this.isHiddenCookie = false;
      }

      const subscriptionDetails = this.user.subscriptionOverview;
      const excludedPlans = ["leads_plus", "leadsplus", "pab"];

      const isPlanExcluded = subscriptionDetails
        && excludedPlans.includes(subscriptionDetails.txt_plan);

      const subId2 = subscriptionDetails && subscriptionDetails.id_chargebee && subscriptionDetails.id_chargebee.String;
      console.log('[RELATIONSHIPS_BANNER] gates', {
        subId: subId2,
        txt_plan: subscriptionDetails && subscriptionDetails.txt_plan,
        isPlanExcluded,
        sunsetMLSUser: this.sunsetMLSUser,
        id_user_purchased: subscriptionDetails && subscriptionDetails.details && subscriptionDetails.details.id_user_purchased,
        current_user_id: this.user.id,
        role: this.user.role,
        is_broker: subscriptionDetails && subscriptionDetails.is_broker,
        is_subaccount: subscriptionDetails && subscriptionDetails.is_subaccount,
        id_item: subscriptionDetails && subscriptionDetails.details && subscriptionDetails.details.id_item,
        isAllowedUser: true,
        relationshipsLicenseCreatedFound: this.relationshipsLicenseCreatedFound,
      });

      if (
        !isPlanExcluded
        && !this.relationshipsLicenseCreatedFound
      ) {
        console.log('[RELATIONSHIPS_BANNER] visibleBanner(): SHOW');
        return true;
      }

      console.log('[RELATIONSHIPS_BANNER] visibleBanner(): HIDE');
      return false
    },
    shouldShowBanner() {
      return this.visibleBanner
    },
  },
  async mounted() {
    this.isHiddenCookie = window.$cookies.get('hideCheckoutLWBanner');
    await this.checkMigrationStatus();
    await this.findRelationshipsLicenseCreated();
    await this.getUserULClients();
    await this.checkRelationshipsOrder();
  },
  methods: {
    ...mapAppLayoutActions([
      'findRelationshipsLicenseCreated',
      'checkMigrationStatus',
      'checkMigrationInProgress',
      'getUserULClients',
      'checkRelationshipsOrder',
      'createRelationshipLicense',
      'startMigration',
    ]),
    generateBannerText(couponCode) {
      const baseText = this.creatingRelationshipsLicense
        ? `<b>Creating your Relationships license and beginning to migrate your LionDesk data...</b>`
        : `<b>Switch to Relationships—your new home for smarter client connections.</b>`;

      return `${baseText}
        LionDesk will be available through <b>${this.sunsetDate}</b>. Use code <b>"${couponCode}"</b> for 20% off an annual Relationships subscription. Update your payment info to get started.`;
    },
    async handleUpgradeNow() {
      // no lwa_id = redirect to connect page
      if (!this.user.lwa_id) {
        window.location.href = `${HC_PROPS.ACCOUNTS_APP_URL}/connect-lone-wolf-account?state=connect&relationships=true`;
        return;
      }
      // lwa_id but no Relationships license = create Relationships license and migration then redirect to app
      if (this.user.lwa_id && !this.ulClientLicenses.includes('Relationships')) {
        this.creatingRelationshipsLicense = true;
        await this.checkMigrationStatus();
        await this.checkMigrationInProgress();
        if (!this.isMigrationComplete) {
          this.isMigratingContacts = true;
          if (!this.isMigrationInProgress) {
            this.startMigration({ userID: this.user.id });
          }
        }
        await this.createRelationshipLicense();
        if (this.createRelationshipsLicenseSuccess) {
          this.clientsPoller = setInterval(() => {
            this.getUserULClients();
          }, 1000);
        }
        return;
      }
      // lwa and relationships license = redirect to checkout page
      const userToken = this.$cookies.get('lwaToken') || this.ulToken;
      window.location.href = `${HC_PROPS.RELATIONSHIPS_URL}/contacts?checkout&jwt=${userToken}`;
    },
    handleHide() {
      window.$cookies.set('hideCheckoutLWBanner', new Date(), null, null, HC_PROPS.MAIN_DOMAIN);
      this.isHiddenCookie = window.$cookies.get('hideCheckoutLWBanner');
      this.$emit('show', false);
    }
  },
  watch: {
    visibleBanner(val) {
      this.$emit('show', val);
    },
    ulClientLicenses: {
      handler(newValue) {
        if (newValue && newValue.includes('Relationships') && this.clientsPoller) {
          clearInterval(this.clientsPoller);
          this.clientsPoller = null;
          setTimeout(() => {
            this.creatingRelationshipsLicense = false;
            window.location = `${HC_PROPS.UL_PLATFORM_URL}/product/launch?a=crm`
          }, 3000);
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.ld-relationships-banner {
  line-height: 1.4;
  height: 56px;
  width: 100%;
  
  .layout {
    margin: 0 24px;
    height: 100%;
  }
  
  .banner-content {
    display: flex;
    align-items: center;
    padding: 0 16px;
    max-width: 1315px;
    
    span {
      padding-left: 4px;
    }
  }
  
  .v-btn.v-btn--icon.v-btn--small {
    margin-left: 12px;
  }
}
</style>
